import { useNovel } from '@/context/novel-context';
import { Close as CloseIcon, Person as PersonIcon } from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

type CollaborationListCollaboratorsProps = {
  currentUserUid?: string;
  isCurrentUserOwner: boolean;
  isUpdatingCollab: boolean;
  updatingMemberId: string | null;
  onRemoveCollaborator: (id: string) => Promise<void>;
  onRemovePendingCollaborator: (email: string) => Promise<void>;
};

export function CollaborationListCollaborators({
  currentUserUid,
  isCurrentUserOwner,
  isUpdatingCollab,
  updatingMemberId,
  onRemoveCollaborator,
  onRemovePendingCollaborator,
}: CollaborationListCollaboratorsProps) {
  const { t } = useTranslation();
  const { novel } = useNovel();
  if (!novel.id) return null;

  const collaboratorsList = Object.entries(novel.collaborators || {});
  const hasCollaborators = collaboratorsList.length > 0 || (novel.pendingCollaborators && novel.pendingCollaborators.length > 0);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
        {t('dashboard.settings.collaborators')}
        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}>
          ({collaboratorsList.length + (novel.pendingCollaborators?.length || 0)})
        </Typography>
      </Typography>

      {!hasCollaborators ? (
        <Box sx={{ p: 2.5, textAlign: 'center', bgcolor: 'action.hover', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.settings.no_collaborators')}
          </Typography>
        </Box>
      ) : (
        <List sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 0 }}>
          {collaboratorsList.map(([collabId, displayName]) => {
            const collabName = collabId === currentUserUid
              ? t('dashboard.settings.you')
              : displayName;
            const collabSub = t('dashboard.settings.collaborator_sub');

            return (
              <ListItem
                key={collabId}
                secondaryAction={
                  isCurrentUserOwner && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      size="small"
                      color="error"
                      onClick={() => onRemoveCollaborator(collabId)}
                      disabled={isUpdatingCollab}
                      sx={{
                        opacity: 0.6,
                        transition: 'opacity 0.2s',
                        '&:hover': { opacity: 1, bgcolor: 'error.lighter' }
                      }}
                    >
                      {updatingMemberId === collabId ? (
                        <CircularProgress size={16} color="error" />
                      ) : (
                        <CloseIcon fontSize="small" />
                      )}
                    </IconButton>
                  )
                }
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateX(3px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    borderColor: 'primary.light',
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`, color: 'primary.contrastText' }}>
                    <PersonIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'sans-serif' }}>
                        {collabName}
                      </Typography>
                      {collabId === currentUserUid && (
                        <Chip label={t('dashboard.settings.you_badge')} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                      )}
                    </Stack>
                  }
                  secondary={collabSub}
                  secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                />
              </ListItem>
            );
          })}

          {novel.pendingCollaborators?.map((email) => (
            <ListItem
              key={email}
              secondaryAction={
                isCurrentUserOwner && (
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    size="small"
                    color="error"
                    onClick={() => onRemovePendingCollaborator(email)}
                    disabled={isUpdatingCollab}
                    sx={{
                      opacity: 0.6,
                      transition: 'opacity 0.2s',
                      '&:hover': { opacity: 1, bgcolor: 'error.lighter' }
                    }}
                  >
                    {updatingMemberId === email ? (
                      <CircularProgress size={16} color="error" />
                    ) : (
                      <CloseIcon fontSize="small" />
                    )}
                  </IconButton>
                )
              }
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px dashed',
                borderColor: 'divider',
                opacity: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateX(3px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  borderColor: 'primary.light',
                }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ background: (theme) => `linear-gradient(135deg, ${theme.palette.action.disabledBackground} 0%, ${theme.palette.secondary.light} 100%)`, color: 'text.secondary' }}>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontStyle: 'italic' }}>
                      {email}
                    </Typography>
                    <Chip
                      label={t('dashboard.settings.pending_badge')}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        bgcolor: 'action.selected',
                        color: 'primary.main',
                        fontWeight: 600
                      }}
                    />
                  </Stack>
                }
                secondary={t('dashboard.settings.invited_sub')}
                secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
export default CollaborationListCollaborators;
